/**
 * Search overlay — the ⌘K panel.
 *
 * Markup: snippets/ag-search-overlay.liquid (shell + empty state) and
 * sections/ag-search.liquid (results). Styling: assets/ag-search.css.
 * Notes: docs/search.md.
 *
 * Plain custom-element-free module, like ag-theme.js and ag-article.js: no
 * import map, so nothing here breaks when Horizon reshuffles its internals.
 *
 * What keeps it quick:
 *  - the state it opens in is already in the DOM, so opening costs no request
 *    and clearing the field costs none either;
 *  - responses are cached by term, so backspacing through a word replays
 *    locally instead of re-asking the server;
 *  - a request in flight is aborted the moment the term changes, so a slow
 *    answer can never overwrite a newer one.
 */

const DEBOUNCE_MS = 120;
/* Long enough that a quick answer never flashes a skeleton, short enough that
   a slow one never looks frozen. */
const SKELETON_MS = 180;
const RESULT_LIMIT = 6;
const CACHE_MAX = 30;

class AgSearch {
  /** @type {HTMLDialogElement} */
  #dialog;
  /** @type {HTMLInputElement} */
  #input;
  /** @type {HTMLElement} */
  #scroll;
  /** @type {HTMLElement} */
  #skeleton;
  /** @type {HTMLElement} */
  #status;
  /** @type {HTMLButtonElement} */
  #clear;
  /** @type {HTMLElement} */
  #emptyState;
  /** @type {HTMLElement | null} */
  #opener = null;

  /** Rendered results by search term. @type {Map<string, string>} */
  #cache = new Map();
  /** @type {AbortController | null} */
  #inflight = null;
  /** @type {number | undefined} */
  #debounce;
  /** @type {number | undefined} */
  #skeletonTimer;
  /** @type {number | undefined} */
  #closeTimer;
  /** Alive only while a close is animating out. @type {AbortController | null} */
  #closeController = null;

  /** Index into the current item list, -1 when nothing is highlighted. */
  #active = -1;
  #itemSeq = 0;

  /**
   * @param {HTMLDialogElement} dialog
   */
  constructor(dialog) {
    this.#dialog = dialog;

    const q = /** @param {string} sel */ (sel) => dialog.querySelector(sel);

    this.#input = /** @type {HTMLInputElement} */ (q('[data-ag-input]'));
    this.#scroll = /** @type {HTMLElement} */ (q('[data-ag-scroll]'));
    this.#skeleton = /** @type {HTMLElement} */ (q('[data-ag-skeleton]'));
    this.#status = /** @type {HTMLElement} */ (q('[data-ag-status]'));
    this.#clear = /** @type {HTMLButtonElement} */ (q('[data-ag-clear]'));
    this.#emptyState = /** @type {HTMLElement} */ (q('[data-ag-empty-state]'));

    /* Localised by Liquid, with [count] and [query] left to fill in — the
       shop sells into markets that do not read English. */
    this.suggestUrl = dialog.dataset.agSuggestUrl || '/search/suggest';
    this.countLabel = dialog.dataset.agCountLabel || '[count] results for "[query]"';
    this.noneLabel = dialog.dataset.agNoneLabel || 'No results for "[query]"';

    this.#bind();
    this.#indexItems();
    this.#labelShortcut();
  }

  /**
   * ⌘ is a Mac key. The markup ships the Mac label because that is the larger
   * half of this shop's desktop traffic; everywhere else it is corrected here,
   * since printing the wrong shortcut is worse than printing none.
   */
  #labelShortcut() {
    const mac = /Mac|iPhone|iPad|iPod/i.test(navigator.platform || navigator.userAgent || '');
    if (mac) return;

    for (const hint of document.querySelectorAll('[data-ag-kbd-hint]')) {
      hint.textContent = 'Ctrl K';
    }
  }

  #bind() {
    document.addEventListener('keydown', this.#onGlobalKey);

    for (const opener of document.querySelectorAll('[data-ag-search-open]')) {
      opener.addEventListener('click', (event) => {
        /* The control is a real link to /search. Only a plain left click is
           taken over; cmd-click, middle click and the rest keep opening the
           results page the way any link would. */
        if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) return;

        event.preventDefault();
        this.open(/** @type {HTMLElement} */ (opener));
      });
    }

    this.#input.addEventListener('input', this.#onInput);
    this.#input.addEventListener('keydown', this.#onFieldKey);
    this.#clear.addEventListener('click', () => this.reset({ focus: true }));

    for (const button of this.#dialog.querySelectorAll('[data-ag-close]')) {
      button.addEventListener('click', () => this.close());
    }

    /* Native `cancel` covers Escape and the platform's own dismiss gesture; we
       take it over so the panel animates out instead of vanishing. */
    this.#dialog.addEventListener('cancel', (event) => {
      event.preventDefault();
      this.close();
    });

    /* The dialog fills the viewport, so anything landing on it rather than on
       the panel is a click on the backdrop. */
    this.#dialog.addEventListener('mousedown', (event) => {
      if (event.target === this.#dialog) this.close();
    });

    /* A pill is a real link to the results page; intercepting a plain click
       turns it into an in-place refinement instead. Modified clicks and
       middle clicks keep the link's own behaviour. */
    this.#scroll.addEventListener('click', (event) => {
      const pill = /** @type {HTMLElement | null} */ (
        /** @type {HTMLElement} */ (event.target).closest('[data-ag-kind="query"][data-ag-term]')
      );
      if (!pill) return;
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) return;

      event.preventDefault();
      this.#input.value = pill.dataset.agTerm || '';
      this.#input.focus();
      this.#onTermChanged({ immediate: true });
    });

    /* Pointer and keyboard fight over the highlight otherwise: moving the
       mouse takes it, typing takes it back. */
    this.#scroll.addEventListener('pointerover', (event) => {
      const item = /** @type {HTMLElement} */ (event.target).closest('[data-ag-item]');
      if (!item) return;
      const index = this.#items.indexOf(/** @type {HTMLElement} */ (item));
      if (index !== -1) this.#setActive(index, { scroll: false });
    });

    /* Keep the caret in the field wherever someone clicks in the panel, so
       typing always continues the query. */
    this.#dialog.addEventListener('mouseup', (event) => {
      const target = /** @type {HTMLElement} */ (event.target);
      if (target.closest('a, button, input')) return;
      this.#input.focus();
    });
  }

  /* ---------- open / close ---------- */

  /**
   * ⌘K on a Mac, Ctrl+K elsewhere, and "/" the way every catalogue and code
   * host has trained people to expect — but never while they are typing
   * somewhere else, which would eat the character.
   * @param {KeyboardEvent} event
   */
  #onGlobalKey = (event) => {
    /* Open again while the panel is still animating out: the dialog counts as
       open for those ~200ms, and without the second half of this test the
       shortcut would be swallowed by a panel on its way off screen. */
    if (this.#dialog.open && !this.#closing) return;

    const key = event.key.toLowerCase();
    const shortcut = (event.metaKey || event.ctrlKey) && key === 'k';
    const slash = key === '/' && !event.metaKey && !event.ctrlKey && !event.altKey;

    if (!shortcut && !slash) return;
    if (slash && this.#isTyping(event.target)) return;

    event.preventDefault();
    this.open();
  };

  /** @param {EventTarget | null} target */
  #isTyping(target) {
    if (!(target instanceof HTMLElement)) return false;
    if (target.isContentEditable) return true;
    return ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName);
  }

  /** True between `close()` and the end of the closing transition. */
  get #closing() {
    return this.#closeController !== null;
  }

  /** Take back a close that has not finished animating. */
  #cancelClose() {
    this.#closeController?.abort();
    this.#closeController = null;
    window.clearTimeout(this.#closeTimer);
  }

  /** @param {HTMLElement} [opener] */
  open(opener) {
    if (this.#dialog.open && !this.#closing) return;

    /* Asked for again mid-close, the panel comes back rather than finishing
       the animation and leaving the request unanswered. Whatever was typed is
       still there, which is what someone reopening this quickly wanted. */
    const reopening = this.#closing;
    this.#cancelClose();

    if (!reopening) {
      this.#opener = opener || (document.activeElement instanceof HTMLElement ? document.activeElement : null);
    }

    if (!this.#dialog.open) this.#dialog.showModal();
    this.#lockScroll(true);

    /* Two frames: `showModal` and the class in the same one would collapse
       into a single style recalculation and skip the transition. */
    requestAnimationFrame(() => {
      requestAnimationFrame(() => this.#dialog.classList.add('is-open'));
    });

    this.#input.focus();
    this.#input.select();
  }

  close() {
    if (!this.#dialog.open || this.#closing) return;

    this.#dialog.classList.remove('is-open');
    this.#abort();

    const done = () => {
      this.#cancelClose();
      if (!this.#dialog.open) return;
      this.#dialog.close();
      this.#lockScroll(false);
      this.reset({ focus: false });
      this.#opener?.focus();
      this.#opener = null;
    };

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      done();
      return;
    }

    /* transitionend is the signal; the timer is the guarantee, for the case
       where the panel was never painted and nothing transitions. Both hang off
       one controller so reopening can call the whole thing off. */
    this.#closeController = new AbortController();
    const panel = this.#dialog.querySelector('.ag-search__panel');
    panel?.addEventListener('transitionend', done, { once: true, signal: this.#closeController.signal });
    this.#closeTimer = window.setTimeout(done, 320);
  }

  /**
   * Horizon's own lock: `html[scroll-lock]` is what base.css reads, and the
   * page scroller already reserves its scrollbar gutter, so nothing shifts.
   * Released only when no other dialog is relying on it.
   * @param {boolean} on
   */
  #lockScroll(on) {
    const root = document.documentElement;
    if (on) {
      root.setAttribute('scroll-lock', '');
      return;
    }
    const others = document.querySelectorAll('dialog[open][scroll-lock], dialog[open] [scroll-lock]');
    if (others.length === 0) root.removeAttribute('scroll-lock');
  }

  /* ---------- querying ---------- */

  #onInput = () => this.#onTermChanged();

  /** @param {{ immediate?: boolean }} [options] */
  #onTermChanged(options) {
    const term = this.#input.value.trim();

    this.#clear.hidden = term.length === 0;
    window.clearTimeout(this.#debounce);

    if (term.length === 0) {
      this.#abort();
      this.reset({ focus: false });
      return;
    }

    /* A cached term is already the answer — waiting out the debounce would
       just add lag to a render we can do this frame. */
    const cached = this.#cache.get(term);
    if (cached) {
      this.#abort();
      this.#render(cached, term);
      return;
    }

    const run = () => this.#fetch(term);
    if (options?.immediate) run();
    else this.#debounce = window.setTimeout(run, DEBOUNCE_MS);
  }

  /** @param {string} term */
  async #fetch(term) {
    this.#abort();

    const controller = new AbortController();
    this.#inflight = controller;

    this.#skeletonTimer = window.setTimeout(() => this.#showSkeleton(true), SKELETON_MS);

    const url = new URL(this.suggestUrl, window.location.origin);
    url.searchParams.set('q', term);
    url.searchParams.set('section_id', 'ag-search');
    url.searchParams.set('resources[type]', 'product,collection,article,page,query');
    url.searchParams.set('resources[limit]', String(RESULT_LIMIT));
    /* Per type, not shared: otherwise one crowded type starves the others and
       the panel comes back as six posters and nothing else. */
    url.searchParams.set('resources[limit_scope]', 'each');
    url.searchParams.set('resources[options][unavailable_products]', 'last');
    url.searchParams.set('resources[options][fields]', 'title,product_type,variants.title,tag,vendor');

    try {
      const response = await fetch(url, { signal: controller.signal });
      if (!response.ok) throw new Error(`Search failed: ${response.status}`);

      const html = await response.text();
      const results = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-ag-search-results]');
      if (!results) throw new Error('Search response carried no results');

      this.#remember(term, results.outerHTML);
      if (controller.signal.aborted) return;
      this.#render(results.outerHTML, term);
    } catch (error) {
      if (controller.signal.aborted) return;
      /* Leave whatever is on screen in place and let the field carry on. The
         submit button still reaches the full results page, so a failed
         suggestion never becomes a dead end. */
      console.error(error);
      this.#showSkeleton(false);
    } finally {
      if (this.#inflight === controller) this.#inflight = null;
    }
  }

  #abort() {
    window.clearTimeout(this.#skeletonTimer);
    this.#inflight?.abort();
    this.#inflight = null;
    this.#showSkeleton(false);
  }

  /**
   * @param {string} term
   * @param {string} html
   */
  #remember(term, html) {
    this.#cache.set(term, html);
    if (this.#cache.size > CACHE_MAX) {
      this.#cache.delete(/** @type {string} */ (this.#cache.keys().next().value));
    }
  }

  /* ---------- rendering ---------- */

  /**
   * @param {string} html
   * @param {string} term
   */
  #render(html, term) {
    this.#showSkeleton(false);

    const current = this.#scroll.querySelector('[data-ag-search-results]');
    const next = /** @type {HTMLElement} */ (
      /** @type {HTMLElement} */ (document.createRange().createContextualFragment(html).firstElementChild)
    );

    /* The empty state is markup we were handed once and could only get again
       by asking the server, so it is detached and kept rather than replaced. */
    if (current === this.#emptyState) {
      current.remove();
      this.#scroll.appendChild(next);
    } else if (current) {
      current.replaceWith(next);
    } else {
      this.#scroll.appendChild(next);
    }

    const count = Number(next.dataset.agCount || 0);

    /* Never a dead end: with nothing found, the popular searches come back
       under the message as something to click. */
    if (count === 0) {
      const quick = this.#emptyState.querySelector('.ag-search__quick');
      if (quick) {
        const cols = document.createElement('div');
        cols.className = 'ag-search__cols';
        cols.appendChild(quick.cloneNode(true));
        next.appendChild(cols);
      }
    }

    this.#scroll.scrollTop = 0;
    this.#indexItems();
    this.#announce(term, count);

    /* One event for anything that wants to log queries — and the ones that
       find nothing are the list worth reading. */
    document.dispatchEvent(
      new CustomEvent('ag:search', { detail: { query: term, count }, bubbles: true })
    );
  }

  /** @param {{ focus: boolean }} options */
  reset(options) {
    window.clearTimeout(this.#debounce);
    this.#abort();

    this.#input.value = '';
    this.#clear.hidden = true;

    const current = this.#scroll.querySelector('[data-ag-search-results]');
    if (current && current !== this.#emptyState) current.replaceWith(this.#emptyState);
    else if (!current) this.#scroll.appendChild(this.#emptyState);
    this.#emptyState.hidden = false;

    this.#scroll.scrollTop = 0;
    this.#status.textContent = '';
    this.#indexItems();

    if (options.focus) this.#input.focus();
  }

  /** @param {boolean} on */
  #showSkeleton(on) {
    window.clearTimeout(this.#skeletonTimer);
    this.#skeleton.hidden = !on;

    const results = this.#scroll.querySelector('[data-ag-search-results]');
    if (results instanceof HTMLElement) results.hidden = on;
  }

  /**
   * @param {string} term
   * @param {number} count
   */
  #announce(term, count) {
    const template = count ? this.countLabel : this.noneLabel;
    this.#status.textContent = template.replace('[count]', String(count)).replace('[query]', term);
  }

  /* ---------- keyboard ---------- */

  /** Everything currently on screen, in reading order. */
  get #items() {
    return /** @type {HTMLElement[]} */ (
      Array.from(this.#scroll.querySelectorAll('[data-ag-item]')).filter(
        /* The skeleton and the parked results block are both `hidden`, and
           neither should be reachable with the arrow keys. */
        (item) => !item.closest('[hidden]')
      )
    );
  }

  /**
   * The field never gives up focus, so the highlight is announced through
   * `aria-activedescendant` and each item needs an id to be pointed at.
   */
  #indexItems() {
    this.#active = -1;
    this.#input.removeAttribute('aria-activedescendant');

    const items = this.#items;
    for (const item of items) {
      if (!item.id) item.id = `ag-search-item-${(this.#itemSeq += 1)}`;
      item.setAttribute('role', 'option');
      item.setAttribute('tabindex', '-1');
      item.classList.remove('is-active');
      /* The options sit in list items for layout. Left as list items they
         would be announced between the listbox and its options, so the
         wrapper is taken out of the tree here rather than in four templates. */
      item.parentElement?.setAttribute('role', 'none');
    }

    this.#input.setAttribute('aria-expanded', items.length > 0 ? 'true' : 'false');
  }

  /**
   * @param {number} index
   * @param {{ scroll?: boolean }} [options]
   */
  #setActive(index, options) {
    const items = this.#items;
    for (const item of items) {
      item.classList.remove('is-active');
      item.removeAttribute('aria-selected');
    }

    this.#active = index;

    const item = items[index];
    if (!item) {
      this.#input.removeAttribute('aria-activedescendant');
      return;
    }

    item.classList.add('is-active');
    item.setAttribute('aria-selected', 'true');
    this.#input.setAttribute('aria-activedescendant', item.id);

    if (options?.scroll !== false) {
      item.scrollIntoView({ block: 'nearest', behavior: 'instant' });
    }
  }

  /**
   * Rows are read off the layout rather than assumed, so the same code walks
   * a three-up product grid, a one-up list and the two-column phone layout:
   * down goes to the next row and keeps its place across, as the eye expects.
   * @param {HTMLElement[]} items
   */
  #rows(items) {
    /** @type {HTMLElement[][]} */
    const rows = [];
    let top = null;

    for (const item of items) {
      const itemTop = Math.round(item.getBoundingClientRect().top);
      /* A few pixels of slack: cards in one row rarely align to the pixel. */
      if (top === null || Math.abs(itemTop - top) > 6) {
        rows.push([item]);
        top = itemTop;
      } else {
        rows[rows.length - 1].push(item);
      }
    }

    return rows;
  }

  /** @param {'up' | 'down' | 'left' | 'right'} direction */
  #move(direction) {
    const items = this.#items;
    if (items.length === 0) return;

    if (this.#active === -1) {
      this.#setActive(direction === 'up' ? items.length - 1 : 0);
      return;
    }

    const current = items[this.#active];
    const rows = this.#rows(items);
    const rowIndex = rows.findIndex((row) => row.includes(current));
    const column = rows[rowIndex].indexOf(current);

    if (direction === 'left' || direction === 'right') {
      const step = direction === 'left' ? -1 : 1;
      const next = rows[rowIndex][column + step];
      if (next) this.#setActive(items.indexOf(next));
      return;
    }

    const step = direction === 'up' ? -1 : 1;
    const nextRow = rows[rowIndex + step];

    /* Off the end in either direction drops the highlight rather than
       wrapping: the field is where you land, and typing continues. */
    if (!nextRow) {
      this.#setActive(-1);
      return;
    }

    const next = nextRow[Math.min(column, nextRow.length - 1)];
    this.#setActive(items.indexOf(next));
  }

  /** @param {KeyboardEvent} event */
  #onFieldKey = (event) => {
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        this.#move('down');
        break;

      case 'ArrowUp':
        event.preventDefault();
        this.#move('up');
        break;

      /* Only once something is highlighted, so the arrows still move the
         caret through what has been typed. */
      case 'ArrowLeft':
        if (this.#active === -1) return;
        event.preventDefault();
        this.#move('left');
        break;

      case 'ArrowRight':
        if (this.#active === -1) return;
        event.preventDefault();
        this.#move('right');
        break;

      case 'Tab':
        if (this.#items.length === 0) return;
        event.preventDefault();
        this.#move(event.shiftKey ? 'up' : 'down');
        break;

      case 'Enter': {
        const item = this.#items[this.#active];
        if (!item) return; // let the form submit to the results page
        event.preventDefault();
        item.click();
        break;
      }

      case 'Escape':
        /* Clear first, close second: the same key backs out one step at a
           time rather than throwing away a half-typed query. */
        if (this.#input.value.length > 0) {
          event.preventDefault();
          event.stopPropagation();
          this.reset({ focus: true });
        }
        break;

      default:
        break;
    }
  };
}

const dialog = document.getElementById('ag-search');
if (dialog instanceof HTMLDialogElement) new AgSearch(dialog);
